import { ENV } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';

declare global {
  interface Window {
    rudderanalytics: {
      load: (writeKey: string, dataPlaneUrl: string, options?: Record<string, unknown>) => void;
      page: () => void;
      track: (event: string, properties?: Record<string, unknown>) => void;
      identify: (userId: string, traits?: Record<string, unknown>) => void;
      reset: () => void;
      ready: (callback: () => void) => void;
    };
  }
}

// Analytics initialization constants
const MAX_ANALYTICS_RETRIES = 30; // ~3 seconds total
const ANALYTICS_RETRY_INTERVAL_MS = 100;

export const initializeAnalytics = () => {
  const { RUDDERSTACK_WRITE_KEY, RUDDERSTACK_DATA_PLANE_URL } = ENV;

  if (!RUDDERSTACK_WRITE_KEY || !RUDDERSTACK_DATA_PLANE_URL) {
    logger.analytics.warn('RudderStack environment variables not set. Analytics disabled.');
    return;
  }

  let retryCount = 0;

  // Wait for SDK to load, then initialize
  const initWhenReady = () => {
    if (typeof window === 'undefined') return;

    if (window.rudderanalytics && typeof window.rudderanalytics.load === 'function') {
      try {
        window.rudderanalytics.load(RUDDERSTACK_WRITE_KEY, RUDDERSTACK_DATA_PLANE_URL, {});

        window.rudderanalytics.ready(() => {
          // Page tracking handled by usePageTracking hook
        });

        logger.analytics.info('RudderStack SDK initialized successfully.');
      } catch (error) {
        logger.analytics.error('RudderStack initialization failed', { error });
      }
    } else {
      retryCount++;
      if (retryCount < MAX_ANALYTICS_RETRIES) {
        setTimeout(initWhenReady, ANALYTICS_RETRY_INTERVAL_MS);
      } else {
        logger.analytics.error(
          `RudderStack SDK not ready after ${MAX_ANALYTICS_RETRIES} attempts. Analytics disabled.`
        );
      }
    }
  };

  initWhenReady();
};

// Track last identify call to prevent duplicate calls
let lastIdentifyUserId: string | null = null;
let lastIdentifyTime: number = 0;
const DUPLICATE_PREVENTION_MS = 1000; // 1 second to prevent duplicate calls

// Analytics helper functions
export const analytics = {
  track: (event: string, properties?: Record<string, unknown>) => {
    if (window.rudderanalytics) {
      try {
        window.rudderanalytics.track(event, properties);
      } catch (error) {
        logger.analytics.error(`Failed to track event "${event}"`, { error, properties });
      }
    }
  },

  identify: (userId: string, traits?: Record<string, unknown>) => {
    if (!window.rudderanalytics) return;

    const now = Date.now();
    const timeSinceLastIdentify = now - lastIdentifyTime;

    // Skip identify only if it's the same user and called within 1 second (duplicate prevention)
    if (lastIdentifyUserId === userId && timeSinceLastIdentify < DUPLICATE_PREVENTION_MS) {
      // Skip this duplicate identify call
      return;
    }

    // Update tracking
    lastIdentifyUserId = userId;
    lastIdentifyTime = now;

    // Call RudderStack identify with error handling
    try {
      window.rudderanalytics.identify(userId, traits);
    } catch (error) {
      logger.analytics.error(`Failed to identify user "${userId}"`, { error, traits });
    }
  },

  page: () => {
    if (window.rudderanalytics) {
      try {
        window.rudderanalytics.page();
      } catch (error) {
        logger.analytics.error('Failed to track page view', { error });
      }
    }
  },

  reset: () => {
    if (window.rudderanalytics) {
      try {
        window.rudderanalytics.reset();
      } catch (error) {
        logger.analytics.error('Failed to reset analytics', { error });
      }
    }
    // Clear identify tracking on reset
    lastIdentifyUserId = null;
    lastIdentifyTime = 0;
  },
};
